import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  AlertCircle,
  AlertTriangle,
  Key,
  Link as LinkIcon,
  ChevronDown,
} from '@saleshandy/icons';
import {
  FailedTaskDetail,
  FAILED_REASON_LABELS,
} from '../../../../../linkedin-automation/types';
import {
  LinkedInFailureReason,
  LinkedInPlatform,
} from '../../../../../linkedin-automation/enums';
import { SequenceStepType } from '../../../../../sequence/enums';
import { updateTasksRequest } from '../../../../extra-actions';
import { TaskAction } from '../../../../utils/enums';
import {
  getSingleProspectDetails,
  updateProspectProfile,
} from '../../../../../prospect/helpers/prospect.api';
import { getLinkedInIntegrations } from '../../../../../linkedin-automation/helpers/linkedin-automation.api';
import { getPlatformFields } from '../../../../../linkedin-automation/constants';

interface IProps {
  task: FailedTaskDetail;
  show: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

type IconColor = 'red' | 'amber';

interface VariantMeta {
  icon: React.ReactNode;
  iconColor: IconColor;
  title: string;
}

// A field that is missing on the prospect (shFieldId resolved at save time if null)
interface MissingFieldDef {
  platformField: string;
  shFieldId: number | null;
  displayLabel: string;
}

const CloseIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const getVariantMeta = (task: FailedTaskDetail): VariantMeta => {
  switch (task.failedReason) {
    case LinkedInFailureReason.MissingRequiredField:
      return {
        icon: <AlertCircle />,
        iconColor: 'red',
        title: 'Required Prospect Field Missing',
      };
    case LinkedInFailureReason.LockedInCampaign:
    case LinkedInFailureReason.AlreadyConnected:
      return {
        icon: <AlertTriangle />,
        iconColor: 'amber',
        title: 'Prospect is already active in another campaign',
      };
    case LinkedInFailureReason.CampaignNotFound:
      return {
        icon: <AlertTriangle />,
        iconColor: 'amber',
        title: 'Linked Campaign Not Found',
      };
    case LinkedInFailureReason.InvalidApiKey:
    case LinkedInFailureReason.IntegrationInactive:
      return {
        icon: <Key />,
        iconColor: 'red',
        title: 'API Key Invalid or Deleted',
      };
    case LinkedInFailureReason.InvalidLinkedInUrl:
      return {
        icon: <LinkIcon />,
        iconColor: 'amber',
        title: 'Invalid LinkedIn URL',
      };
    case LinkedInFailureReason.RateLimited:
    case LinkedInFailureReason.ServerError:
    case LinkedInFailureReason.Forbidden:
      return {
        icon: <AlertCircle />,
        iconColor: 'red',
        title: 'External Service Error',
      };
    default:
      return {
        icon: <AlertCircle />,
        iconColor: 'red',
        title: FAILED_REASON_LABELS[task.failedReason] || 'Task Failed',
      };
  }
};

const FailedReasonPopup: React.FC<IProps> = ({ task, show, onClose, onRefresh }) => {
  const dispatch = useDispatch();
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [showRawError, setShowRawError] = useState(false);
  const [missingFieldValues, setMissingFieldValues] = useState<
    Record<string, string>
  >({});
  const [newLinkedInUrl, setNewLinkedInUrl] = useState('');

  // Loading + resolved missing fields for MissingRequiredField case
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [missingFieldDefs, setMissingFieldDefs] = useState<MissingFieldDef[]>(
    [],
  );
  // Fetched current LinkedIn URL for InvalidLinkedInUrl case
  const [fetchedLinkedInUrl, setFetchedLinkedInUrl] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!show) return;
    const isMissingField =
      task.failedReason === LinkedInFailureReason.MissingRequiredField;
    const isInvalidUrl =
      task.failedReason === LinkedInFailureReason.InvalidLinkedInUrl;
    if (!isMissingField && !isInvalidUrl) return;

    const prospectId = task.prospectId ?? (task as any).prospect?.[0]?.id;
    if (!prospectId) return;

    setIsLoadingData(true);
    setMissingFieldValues({});
    setMissingFieldDefs([]);
    setFetchedLinkedInUrl(null);

    if (isMissingField) {
      Promise.all([
        getLinkedInIntegrations(),
        getSingleProspectDetails(prospectId),
      ])
        .then(([integrations, prospectResp]) => {
          const intList = integrations ?? [];
          const prospectAttributes: any[] =
            prospectResp?.payload?.attributes ?? [];

          // Derive platform from task.platform, falling back to task.type
          const derivedPlatform =
            task.platform != null
              ? Number(task.platform)
              : task.type === SequenceStepType.AimfoxCampaignPush
              ? LinkedInPlatform.Aimfox
              : task.type === SequenceStepType.HeyReachCampaignPush
              ? LinkedInPlatform.HeyReach
              : null;
          const integrationsToCheck =
            derivedPlatform != null
              ? intList.filter((i) => Number(i.platform) === derivedPlatform)
              : intList;

          const defs: MissingFieldDef[] = [];

          for (const int of integrationsToCheck) {
            const platformFields = getPlatformFields(
              int.platform as LinkedInPlatform,
            );

            for (const field of platformFields) {
              // Only check required non-locked fields
              if (!field.required || field.locked) continue;

              // Get shFieldId from this integration's mapping
              const mapping = int.fieldMappings.find(
                (m) => m.platformField === field.platformField,
              );
              const shFieldId: number | null = mapping?.shFieldId ?? null;
              if (shFieldId == null) continue;

              // Check if the prospect attribute for this shFieldId is empty
              const prospectAttr = prospectAttributes.find(
                (a: any) => (a.fieldRef?.id ?? a.fieldRefId) === shFieldId,
              );
              const rawValue = prospectAttr?.attributeValue;
              let hasValue: boolean;
              if (rawValue == null || rawValue === '') {
                hasValue = false;
              } else if (typeof rawValue === 'string') {
                hasValue = rawValue.trim() !== '';
              } else if (Array.isArray(rawValue)) {
                hasValue = rawValue.length > 0;
              } else {
                hasValue = true;
              }

              // Avoid duplicate entries for the same platformField
              if (
                !hasValue &&
                !defs.some((d) => d.platformField === field.platformField)
              ) {
                defs.push({
                  platformField: field.platformField,
                  shFieldId,
                  displayLabel: field.label,
                });
              }
            }
          }

          setMissingFieldDefs(defs);
        })
        .catch((err) => {
          setRetryError(
            `Failed to load prospect details: ${
              err?.message ?? 'Unknown error'
            }`,
          );
        })
        .finally(() => {
          setIsLoadingData(false);
        });
    } else {
      // InvalidLinkedInUrl — just fetch prospect to get current LinkedIn URL
      getSingleProspectDetails(prospectId)
        .then((prospectResp) => {
          const prospectAttributes: any[] =
            prospectResp?.payload?.attributes ?? [];
          const attr = prospectAttributes.find(
            (a: any) =>
              a.fieldRef?.label?.replace(/\s+/g, '').toLowerCase() ===
              'linkedin',
          );
          setFetchedLinkedInUrl(
            attr?.attributeValue ?? task.currentLinkedInUrl ?? null,
          );
        })
        .catch(() => {
          setFetchedLinkedInUrl(task.currentLinkedInUrl ?? null);
        })
        .finally(() => {
          setIsLoadingData(false);
        });
    }
  }, [show, task.failedReason, task.platform, task.prospectId]);

  if (!show) return null;

  const isAimfox =
    task.platform === LinkedInPlatform.Aimfox ||
    (!task.platform && task.type === SequenceStepType.AimfoxCampaignPush);
  const platformName = isAimfox ? 'Aimfox' : 'HeyReach';
  const platformUrl = isAimfox
    ? 'https://app.aimfox.com'
    : 'https://app.heyreach.io';

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryError(null);
    const result = await dispatch(
      updateTasksRequest({
        taskIds: [task.id],
        taskAction: TaskAction.RetryLinkedInTask,
      }) as any,
    );
    if (updateTasksRequest.fulfilled.match(result)) {
      onClose();
      onRefresh?.();
    } else {
      setRetryError('Retry failed. Please try again.');
    }
    setIsRetrying(false);
  };

  const handleSaveAndRetry = async () => {
    setIsRetrying(true);
    setRetryError(null);
    try {
      const prospectId = task.prospectId ?? (task as any).prospect?.[0]?.id;

      if (task.failedReason === LinkedInFailureReason.MissingRequiredField) {
        if (prospectId && missingFieldDefs.length > 0) {
          // Re-fetch prospect to resolve any shFieldId that was null at load time
          const prospectResp = await getSingleProspectDetails(prospectId);
          const prospectAttributes: any[] =
            prospectResp?.payload?.attributes ?? [];

          const attributes = missingFieldDefs
            .filter(({ platformField }) =>
              missingFieldValues[platformField]?.trim(),
            )
            .map(({ platformField, shFieldId, displayLabel }) => {
              let resolvedId = shFieldId;
              if (resolvedId == null) {
                const labelNorm = displayLabel
                  .replace(/\s+/g, '')
                  .toLowerCase();
                const attr = prospectAttributes.find(
                  (a: any) =>
                    a.fieldRef?.label?.replace(/\s+/g, '').toLowerCase() ===
                    labelNorm,
                );
                resolvedId = attr?.fieldRef?.id ?? attr?.fieldRefId ?? null;
              }
              return resolvedId != null
                ? {
                    attributeValue: missingFieldValues[platformField].trim(),
                    fieldRef: resolvedId,
                  }
                : null;
            })
            .filter(Boolean) as Array<{
            attributeValue: string;
            fieldRef: number;
          }>;

          if (attributes.length > 0) {
            await updateProspectProfile(prospectId, attributes);
          }
        }
      } else if (
        task.failedReason === LinkedInFailureReason.InvalidLinkedInUrl
      ) {
        if (prospectId && newLinkedInUrl.trim()) {
          const prospectResp = await getSingleProspectDetails(prospectId);
          const prospectAttributes: any[] =
            prospectResp?.payload?.attributes ?? [];

          const attr = prospectAttributes.find(
            (a: any) =>
              a.fieldRef?.label?.replace(/\s+/g, '').toLowerCase() ===
              'linkedin',
          );
          const fieldRef = attr?.fieldRef?.id ?? attr?.fieldRefId;

          if (fieldRef != null) {
            await updateProspectProfile(prospectId, [
              { attributeValue: newLinkedInUrl.trim(), fieldRef },
            ]);
          }
        }
      }
    } catch {
      setRetryError('Failed to update prospect fields. Please try again.');
      setIsRetrying(false);
      return;
    }
    const result = await dispatch(
      updateTasksRequest({
        taskIds: [task.id],
        taskAction: TaskAction.RetryLinkedInTask,
      }) as any,
    );
    if (updateTasksRequest.fulfilled.match(result)) {
      onClose();
      onRefresh?.();
    } else {
      setRetryError('Retry failed. Please try again.');
    }
    setIsRetrying(false);
  };

  const { icon, iconColor, title } = getVariantMeta(task);

  const renderBody = () => {
    switch (task.failedReason) {
      case LinkedInFailureReason.MissingRequiredField: {
        if (isLoadingData || isRetrying) {
          return (
            <div className="failed-reason-popup__loading">
              <div className="failed-reason-popup__spinner" />
              <span className="regular-2 gray-txt-11">
                {isRetrying ? 'Saving changes…' : 'Loading prospect details…'}
              </span>
            </div>
          );
        }

        if (missingFieldDefs.length === 0) {
          return (
            <p className="regular-2 gray-txt-9">
              No missing fields were detected. You can retry the task directly.
            </p>
          );
        }

        return (
          <>
            <p className="regular-2 gray-txt-9">
              This prospect is missing required field(s) needed to add them to
              the campaign. Please update the prospect details to continue.
            </p>
            <div className="failed-reason-popup__missing-fields">
              {missingFieldDefs.map(({ platformField, displayLabel }) => (
                // eslint-disable-next-line jsx-a11y/label-has-associated-control
                <label
                  key={platformField}
                  className="failed-reason-popup__field-label"
                >
                  <span className="semibold-1 gray-txt-9">{displayLabel}</span>
                  <input
                    type="text"
                    className="failed-reason-popup__field-input"
                    placeholder={`Enter ${displayLabel}...`}
                    value={missingFieldValues[platformField] || ''}
                    onChange={(e) => {
                      const { value } = e.target;
                      setMissingFieldValues((prev) => ({
                        ...prev,
                        [platformField]: value,
                      }));
                    }}
                  />
                </label>
              ))}
            </div>
          </>
        );
      }

      case LinkedInFailureReason.LockedInCampaign:
      case LinkedInFailureReason.AlreadyConnected:
        return (
          <p className="regular-2 gray-txt-9">
            This prospect is currently active in another {platformName}{' '}
            campaign. A prospect can only be part of one campaign at a time.
            Remove the prospect from the other campaign or wait until it
            finishes, then try again.
          </p>
        );

      case LinkedInFailureReason.CampaignNotFound:
        return (
          <p className="regular-2 gray-txt-9">
            The {platformName} campaign linked to this step has been deleted or
            is no longer available. To continue pushing prospects, please update
            the campaign in the sequence step configuration.
          </p>
        );

      case LinkedInFailureReason.InvalidApiKey:
      case LinkedInFailureReason.IntegrationInactive:
        return (
          <p className="regular-2 gray-txt-9">
            The API key used to connect with {platformName} is no longer valid.
            It may have been deleted or revoked from the {platformName}{' '}
            dashboard. Please reconnect your {platformName} account in{' '}
            <strong>Settings → Integrations</strong>.
          </p>
        );

      case LinkedInFailureReason.InvalidLinkedInUrl: {
        if (isLoadingData) {
          return (
            <div className="failed-reason-popup__loading">
              <div className="failed-reason-popup__spinner" />
              <span className="regular-2 gray-txt-11">
                Loading prospect details…
              </span>
            </div>
          );
        }
        return (
          <>
            <p className="regular-2 gray-txt-9">
              The LinkedIn URL provided is not valid. Please update it with a
              correct LinkedIn profile URL format.
            </p>
            <div className="failed-reason-popup__url-fields">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="failed-reason-popup__field-label">
                <span className="semibold-1 gray-txt-9">Current URL</span>
                <input
                  type="text"
                  className="form-control regular-2 mt-1"
                  value={fetchedLinkedInUrl || '(none)'}
                  readOnly
                />
              </label>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="failed-reason-popup__field-label">
                <span className="semibold-1 gray-txt-9">LinkedIn URL</span>
                <input
                  type="text"
                  className="form-control regular-2 mt-1"
                  placeholder="https://linkedin.com/in/username"
                  value={newLinkedInUrl}
                  onChange={(e) => setNewLinkedInUrl(e.target.value)}
                />
              </label>
            </div>
          </>
        );
      }

      case LinkedInFailureReason.RateLimited:
      case LinkedInFailureReason.ServerError:
      case LinkedInFailureReason.Forbidden:
        return (
          <>
            <p className="regular-2 gray-txt-9">
              The connected platform returned an unexpected error while
              processing this request.
            </p>
            {task.rawApiError && (
              <div className="failed-reason-popup__raw-error-section">
                <button
                  type="button"
                  className={[
                    'failed-reason-popup__view-details-btn',
                    showRawError &&
                      'failed-reason-popup__view-details-btn--open',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setShowRawError((prev) => !prev)}
                >
                  <ChevronDown />
                  View Details
                </button>
                {showRawError && (
                  <pre className="failed-reason-popup__raw-error">
                    {task.rawApiError}
                  </pre>
                )}
              </div>
            )}
          </>
        );

      default:
        return (
          <p className="regular-2 gray-txt-9">
            {task.rawApiError || 'This task failed with an unknown error.'}
          </p>
        );
    }
  };

  const renderFooter = () => {
    switch (task.failedReason) {
      case LinkedInFailureReason.MissingRequiredField:
        return (
          <>
            <button
              type="button"
              className="failed-reason-popup__btn failed-reason-popup__btn--cancel"
              onClick={onClose}
              disabled={isRetrying}
            >
              Cancel
            </button>
            <button
              type="button"
              className="failed-reason-popup__btn failed-reason-popup__btn--primary"
              disabled={
                isRetrying || isLoadingData || missingFieldDefs.length === 0
              }
              onClick={handleSaveAndRetry}
            >
              {isRetrying ? 'Saving...' : 'Save & Retry'}
            </button>
          </>
        );

      case LinkedInFailureReason.InvalidLinkedInUrl:
        return (
          <>
            <button
              type="button"
              className="failed-reason-popup__btn failed-reason-popup__btn--cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="failed-reason-popup__btn failed-reason-popup__btn--primary"
              disabled={isRetrying || isLoadingData || !newLinkedInUrl.trim()}
              onClick={handleSaveAndRetry}
            >
              {isRetrying ? 'Saving...' : 'Save & Retry'}
            </button>
          </>
        );

      case LinkedInFailureReason.CampaignNotFound: {
        const sequenceId =
          task.sequenceId ?? (task as any)?.sequence?.sequenceId;
        return (
          <>
            <button
              type="button"
              className="failed-reason-popup__btn failed-reason-popup__btn--cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="failed-reason-popup__btn failed-reason-popup__btn--primary"
              onClick={() => {
                onClose();
                if (sequenceId) {
                  window.open(
                    `${process.env.REACT_APP_BASE_URL}/sequence/${sequenceId}/steps`,
                    '_self',
                  );
                }
              }}
            >
              Go to Sequence Step
            </button>
          </>
        );
      }

      case LinkedInFailureReason.InvalidApiKey:
      case LinkedInFailureReason.IntegrationInactive:
        return (
          <>
            <button
              type="button"
              className="failed-reason-popup__btn failed-reason-popup__btn--cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <a
              href={platformUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="failed-reason-popup__btn failed-reason-popup__btn--primary"
            >
              Go to {platformName}
            </a>
          </>
        );

      default:
        return (
          <>
            <button
              type="button"
              className="failed-reason-popup__btn failed-reason-popup__btn--cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="failed-reason-popup__btn failed-reason-popup__btn--primary"
              disabled={isRetrying}
              onClick={handleRetry}
            >
              {isRetrying ? 'Retrying...' : 'Retry'}
            </button>
          </>
        );
    }
  };

  return (
    <>
      <div
        className="failed-reason-popup__backdrop"
        role="button"
        tabIndex={0}
        aria-label="Close modal"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onClose();
        }}
      />
      <div
        className="failed-reason-popup__container"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="failed-reason-popup__dialog"
          role="dialog"
          aria-modal="true"
        >
          {/* Header */}
          <div className="failed-reason-popup__header">
            <div
              className={`failed-reason-popup__icon-circle failed-reason-popup__icon-circle--${iconColor}`}
            >
              {icon}
            </div>
            <h5 className="failed-reason-popup__title">{title}</h5>
            <button
              type="button"
              className="failed-reason-popup__close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Body */}
          <div className="failed-reason-popup__body">
            {renderBody()}
            {retryError && (
              <p className="regular-1 red-txt-12 mt-2 mb-0">{retryError}</p>
            )}
          </div>

          {/* Footer */}
          <div className="failed-reason-popup__footer">{renderFooter()}</div>
        </div>
      </div>
    </>
  );
};

export default FailedReasonPopup;
